import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

// Three states, deliberately distinct: still checking, signed out, and signed in but not an admin.
// The last one exists because `is_admin()` returning false is RLS working correctly — it would
// otherwise present as an empty catalogue, which sends you debugging the wrong thing entirely.
type Auth =
  | { status: 'loading' }
  | { status: 'signed-out' }
  | { status: 'not-admin'; email: string | null }
  | { status: 'admin'; session: Session };

const AuthContext = createContext<Auth>({ status: 'loading' });

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<Auth>({ status: 'loading' });

  useEffect(() => {
    let active = true;

    async function resolve(session: Session | null) {
      if (!session) {
        if (active) setAuth({ status: 'signed-out' });
        return;
      }
      // is_admin() keeps its EXECUTE grant for `authenticated` precisely so a client can ask
      // (ADR 0033). A thrown error is treated as not-admin: the Console has nothing to show
      // someone whose admin status cannot be established.
      const { data, error } = await supabase.rpc('is_admin');
      if (!active) return;
      if (error || data !== true) {
        setAuth({ status: 'not-admin', email: session.user.email ?? null });
        return;
      }
      setAuth({ status: 'admin', session });
    }

    supabase.auth.getSession().then(({ data }) => resolve(data.session));
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, session) => {
      resolve(session);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
